import { axiosInstance, axiosInstanceV1 } from "./axiosInstance";

/*
	Quiz y encuestas del Portal de Clase (Parte D, 2026-10-19). Contrato: docs/LMS_D_CONTRATO.md.
	 - personal:  /admin/classPortal/{horario}/_tasks/{post}/_quiz, _quizResults   (v2)
	 - catálogo:  /admin/classPortal/_catalogo/{curso|modulo}/{id}/_items/{item}/_quiz (v2)
	 - alumno:    /scholar/_class/{horario}/_tasks/{post}/_quiz...                 (v1; el alumno sale de la sesión)
	Las respuestas correctas solo llegan al personal; al alumno solo cuando el ajuste "Ver Respuestas" lo permite.
	El reloj del quiz lo cuenta el servidor (deadlineMs + serverNowMs); aquí solo se dibuja.
*/

export type TipoPregunta = "opcion" | "casillas" | "vf" | "corta" | "parrafo"
export type MostrarRespuestas = "nunca" | "entregar" | "vencer" | "devolver"

export interface OpcionQuiz { id?: string; text: string; correct?: boolean }
export interface ImagenQuiz { key: string; name: string; url?: string | null }

/** Pregunta como la ve (y la manda) el personal: con las correctas. */
export interface PreguntaQuiz
{
	id?: number
	type: TipoPregunta
	text: string
	points: number
	explanation: string | null
	image: ImagenQuiz | null
	options: OpcionQuiz[]
	accepted: string[]
}

export interface AjustesQuiz { timeLimitMin: number | null; maxAttempts: number; shuffle: boolean; showAnswers: MostrarRespuestas }

export interface QuizCompleto
{
	settings: AjustesQuiz
	questions: PreguntaQuiz[]
	totalPoints: number
	/** false = heredado del curso/módulo: aquí solo se mira. */
	editable: boolean
	inherited: boolean
	originLabel: string
	/** Ya hay intentos entregados: guardar recalifica a todos contra la versión nueva. */
	hasAttempts: boolean
	hasParagraph: boolean
}

export interface DatosQuiz { settings?: AjustesQuiz; questions?: PreguntaQuiz[] }

// ---------------------------------------------------------------- Resultados (personal)

export type EstadoQuizPersonal = "sin_intentar" | "falta" | "en_curso" | "por_calificar" | "calificada" | "devuelta"

export interface TareaDelQuiz { id: number; title: string; points: number; dueMs: number | null; dueLabel: string | null; closed: boolean; inherited: boolean; hasParagraph: boolean; questions: number }

export interface AlumnoDelQuiz
{
	id: number
	name: string
	initials: string
	photo: string | null
	state: EstadoQuizPersonal
	attempts: number
	inProgress: boolean
	best: number | null
	bestPct: number | null
	lateDiscount: number
	returned: boolean
	toGrade: number
	lastSubmittedLabel: string | null
	late: boolean
}

export interface PreguntaResumen { id: number; order: number; type: TipoPregunta; text: string; points: number; answered: number; correctPct: number | null; avgPoints: number | null }

export interface ResultadosQuiz
{
	task: TareaDelQuiz
	settings: AjustesQuiz
	summary: { students: number; submitted: number; inProgress: number; toGrade: number; unreturned: number; averagePct: number | null }
	students: AlumnoDelQuiz[]
	questions: PreguntaResumen[]
}

export type ResultadoPregunta = "correcta" | "parcial" | "incorrecta" | "sin_respuesta" | "por_calificar"

/** Una pregunta dentro de un intento: lo que contestó, lo que vale y (si se puede ver) la correcta. */
export interface PreguntaDeIntento
{
	id: number
	type: TipoPregunta
	text: string
	image?: ImagenQuiz | null
	imageUrl?: string | null
	points: number
	explanation?: string | null
	options: OpcionQuiz[]
	accepted?: string[]
	answer: string | string[] | null
	earned?: number | null
	result?: ResultadoPregunta
	needsGrading?: boolean
}

export interface IntentoPersonal
{
	id: number
	number: number
	state: "en_curso" | "entregado" | "calificado"
	startedLabel: string | null
	submittedLabel: string | null
	late: boolean
	lateDays: number
	closedByTime: boolean
	autoPoints: number | null
	manualPoints: number | null
	score: number | null
	lateDiscount: number
	final: number | null
	counts: boolean
	toGrade: number
	questions?: PreguntaDeIntento[]
}

export interface IntentosDeAlumno
{
	student: { id: number; name: string; initials: string; photo: string | null }
	task: TareaDelQuiz
	grade: { points: number | null; lateDiscount: number; returned: boolean; comment: string | null; attemptNumber: number | null } | null
	attempts: IntentoPersonal[]
}

export interface ApiQuizPersonal
{
	get(postId: number): Promise<QuizCompleto>
	save(postId: number, d: DatosQuiz): Promise<QuizCompleto>
	results(postId: number): Promise<ResultadosQuiz>
	student(postId: number, studentId: number): Promise<IntentosDeAlumno>
	grade(postId: number, studentId: number, attemptId: number, d: { points: Record<string, number>; comment?: string; action: "guardar" | "devolver" }): Promise<IntentosDeAlumno>
}

export const apiQuizPersonal = (h: number): ApiQuizPersonal =>
{
	const b = "/admin/classPortal/" + h + "/_tasks/"
	return {
		get: async (p) => (await axiosInstance.get(b + p + "/_quiz")).data,
		save: async (p, d) => (await axiosInstance.put(b + p + "/_quiz", d)).data,
		results: async (p) => (await axiosInstance.get(b + p + "/_quizResults")).data,
		student: async (p, s) => (await axiosInstance.get(b + p + "/_quizResults/" + s)).data,
		grade: async (p, s, a, d) => (await axiosInstance.put(b + p + "/_quizResults/" + s + "/_attempts/" + a, d)).data,
	}
}

/** Quiz de una pieza del curso o del módulo (Catálogo › Cursos › Contenido). */
export const apiQuizCatalogo = (nivel: "curso" | "modulo", id: number) =>
{
	const b = "/admin/classPortal/_catalogo/" + nivel + "/" + id + "/_items/"
	return {
		get: async (itemId: number): Promise<QuizCompleto> => (await axiosInstance.get(b + itemId + "/_quiz")).data,
		save: async (itemId: number, d: DatosQuiz): Promise<QuizCompleto> => (await axiosInstance.put(b + itemId + "/_quiz", d)).data,
	}
}

// ---------------------------------------------------------------- Alumno

export interface IntentoResumen
{
	number: number
	state: "entregado" | "calificado"
	submittedLabel: string | null
	late: boolean
	closedByTime: boolean
	score: number | null
	scoreHidden: boolean
	lateDiscount: number
	final: number | null
	counts: boolean
	pending: boolean
	answersVisible: boolean
}

/** El intento en curso: sin correctas, sin explicación, en el orden (barajado) que le tocó. */
export interface IntentoEnCurso
{
	number: number
	state: "en_curso"
	startedMs: number
	deadlineMs: number | null
	remainingSec: number | null
	serverNowMs: number
	questions: { id: number; type: TipoPregunta; text: string; imageUrl: string | null; points: number; options: { id: string; text: string }[] }[]
	answers: Record<string, string | string[]>
	answered: number
	total: number
}

export interface PortadaQuiz
{
	id: number
	title: string
	body: string | null
	unit: string | null
	points: number
	questions: number
	dueMs: number | null
	dueLabel: string | null
	overdue: boolean
	acceptLate: boolean
	latePenaltyPct: number
	closed: boolean
	settings: { timeLimitMin: number | null; maxAttempts: number; showAnswers: MostrarRespuestas }
	attemptsUsed: number
	attemptsLeft: number
	canStart: boolean
	blocked: { code: string; message: string } | null
	current: IntentoEnCurso | null
	attempts: IntentoResumen[]
	grade: { points: number | null; pointsHidden: boolean; lateDiscount: number; comment: string | null } | null
	answersVisible: boolean
	answersNote: string | null
	serverNowMs: number
	/** Solo al entregar: número del intento recién entregado. */
	submitted?: number
}

export interface IntentoRevisado
{
	number: number
	state: "entregado" | "calificado"
	submittedLabel: string | null
	score: number | null
	scoreHidden: boolean
	answersVisible: boolean
	questions: PreguntaDeIntento[]
}

export interface ApiQuizAlumno
{
	cover(postId: number): Promise<PortadaQuiz>
	start(postId: number): Promise<{ attempt: IntentoEnCurso }>
	answer(postId: number, questionId: number, value: string | string[] | null): Promise<{ saved: boolean; answered: number; total: number; remainingSec: number | null; serverNowMs: number }>
	submit(postId: number): Promise<PortadaQuiz>
	attempt(postId: number, n: number): Promise<IntentoRevisado>
	/** Al cerrar o esconder la página: manda lo último escrito aunque la página se vaya (fetch con keepalive). */
	answerAlSalir(postId: number, questionId: number, value: string | string[] | null): void
}

export const apiQuizAlumno = (h: number): ApiQuizAlumno =>
{
	const b = "/scholar/_class/" + h + "/_tasks/"
	return {
		cover: async (p) => (await axiosInstanceV1.get(b + p + "/_quiz")).data,
		start: async (p) => (await axiosInstanceV1.post(b + p + "/_quiz/_start")).data,
		answer: async (p, questionId, value) => (await axiosInstanceV1.put(b + p + "/_quiz/_answer", { questionId, value })).data,
		submit: async (p) => (await axiosInstanceV1.post(b + p + "/_quiz/_submit")).data,
		attempt: async (p, n) => (await axiosInstanceV1.get(b + p + "/_quiz/_attempts/" + n)).data,
		answerAlSalir: (p, questionId, value) => {
			try {
				const token = localStorage.getItem("accessToken")
				fetch((process.env.REACT_APP_API_URL || "") + b + p + "/_quiz/_answer", {
					method: "PUT", keepalive: true, body: JSON.stringify({ questionId, value }),
					headers: { "Content-Type": "application/json", Accept: "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
				}).catch(() => undefined)
			} catch { /* sin red o sin almacenamiento: se queda lo último que sí se guardó */ }
		},
	}
}

// ---------------------------------------------------------------- Encuestas (en el Muro, sin nota)

export type ResultadosEncuesta = "votar" | "cerrar" | "nunca"

export interface VotanteEncuesta { id: number; name: string; initials: string; photo: string | null }

export interface OpcionEncuesta
{
	id: number
	text: string
	/** Personal siempre; alumno solo si resultsVisible. */
	votes?: number
	pct?: number
	/** Solo personal y solo si NO es anónima (el servidor no manda nombres de una anónima). */
	voters?: VotanteEncuesta[]
}

export interface BloqueEncuesta
{
	multiple: boolean
	anonymous: boolean
	results: ResultadosEncuesta
	closesMs: number | null
	closesInput?: string | null
	closesLabel: string | null
	closed: boolean
	/** Cuántos votaron (alumno: null si aún no ve resultados). */
	voters: number | null
	options: OpcionEncuesta[]
	// personal
	students?: number
	hasVotes?: boolean
	// alumno
	canVote?: boolean
	voted?: boolean
	myVotes?: number[]
	resultsVisible?: boolean
}

export interface DatosEncuesta
{
	options?: (string | { id: number; text: string })[]
	multiple?: boolean
	anonymous?: boolean
	closesAt?: string | null
	results?: ResultadosEncuesta
	/** true cierra ya; false la reabre (quita el cierre). */
	close?: boolean
}
